import React, { useState, useRef, useEffect } from "react";
import {
  ProgressMeterCircle,
  InnerCircle,
  ProgressText,
  TimeRemaining,
  DataNotAvailable,
  Loading,
  OuterCircle
} from "./styles/ProgressMeter.styles";
import { numberWithCommas } from "../utilities/displayFormatUtils";
import { useInViewport } from '../../../utils/CustomHooks';

type ProgressMeterProps = {
  daysLeft?: number;
  totalDays?: number | null;
  showEstimated?: boolean;
  maxMilage?: number;
  daysLeftPercent: number;
  isLoading: boolean;
};

const ProgressMeter = ({
  daysLeft = 0,
  totalDays = 0,
  daysLeftPercent,
  showEstimated = true,
  maxMilage = 0,
  isLoading
}: ProgressMeterProps) => {
  const [triggerAnimation, setTriggerAnimation] = useState(false);
  const animationTimeout = useRef<NodeJS.Timeout>();
  const progressMeterRef: any = useRef(null);
  const isInViewport = useInViewport(progressMeterRef, 'progress-meter');

  const triggerAnimationInit = () => {
    setTriggerAnimation(true);
  }

  useEffect(() => {
    if (isInViewport) {
      clearTimeout(animationTimeout.current as NodeJS.Timeout);
      animationTimeout.current = setTimeout(() => {
        triggerAnimationInit();
      }, 800);      
    }
  }, [isInViewport]);

  useEffect(() => {
    return () => {
      clearTimeout(animationTimeout.current as NodeJS.Timeout);
    }
  }, []);

  const getTimeRemaining = () => {
    if (daysLeft >= 101) {
      const monthsLeft = daysLeft / 365 * 12;
      return {
        timeRemaining: Math.floor(monthsLeft),
        timeRemainingUnitLabel: "Month"
      };
    }
    return {
      timeRemaining: daysLeft,
      timeRemainingUnitLabel: "Day"
    };
  }

  const shouldShowDaysLeft = typeof daysLeft === "number";
  const shouldAnimateDaysLeft = shouldShowDaysLeft && triggerAnimation;
  const progressCircleProps = {
    totalDays,
    hasMaxMilage: maxMilage > 0,
    noData: !shouldShowDaysLeft,
    daysLeftPercent: (shouldAnimateDaysLeft ? daysLeftPercent : "1")
  };

  const { timeRemaining, timeRemainingUnitLabel } = getTimeRemaining();
  const shouldDaysLeftBePlural = timeRemaining !== 1;

  return (
    <div id="progress-meter" ref={progressMeterRef}>
      <ProgressMeterCircle {...progressCircleProps} shouldAnimateDaysLeft={shouldAnimateDaysLeft}>
        <OuterCircle {...progressCircleProps}>
          <InnerCircle>
            <ProgressText {...progressCircleProps}>
              {!shouldShowDaysLeft && (
                isLoading
                  ? (
                    <Loading>
                      <p>Loading...</p>
                    </Loading>
                  )
                  : (
                    <DataNotAvailable>
                      <p>Data Not</p>
                      <p>Available</p>
                    </DataNotAvailable>
                  )
              )}
              {shouldShowDaysLeft && (maxMilage === 0) && (
                <>
                  {showEstimated && <p>Estimated</p>}
                  <TimeRemaining {...progressCircleProps}>
                    {timeRemaining}
                  </TimeRemaining>
                  {shouldDaysLeftBePlural
                    ? <p>{`${timeRemainingUnitLabel}s left`}</p>
                    : <p>{`${timeRemainingUnitLabel} left`}</p>
                  }
                </>
              )}
              {maxMilage > 0 && (
                <>
                  <TimeRemaining {...progressCircleProps}>
                    {timeRemaining}
                  </TimeRemaining>
                  {shouldDaysLeftBePlural
                    ? <p>{`${timeRemainingUnitLabel}s left up to ${numberWithCommas(maxMilage)} miles`}</p>
                    : <p>{`${timeRemainingUnitLabel} left up to ${numberWithCommas(maxMilage)} miles`}</p>
                  }
                </>
              )}
            </ProgressText>
          </InnerCircle>
        </OuterCircle>

        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="120px" height="120px">
          <circle cx="60" cy="60" r="57" strokeLinecap="round" />
        </svg>
      </ProgressMeterCircle>
    </div>
  );
};

export default ProgressMeter;
